import { useEffect, useReducer, useState, useRef, FC, Reducer } from 'react';
import { useInView } from 'react-intersection-observer';
import styles from './index.module.scss';
import {
  initialState,
  insertVideo,
  PercentageArrival,
  percentageArrival,
  ActionType,
  PERCENTAGE,
  deleteVideo,
  getPrimeVideoSetting,
  VideoAccessor,
} from './logics/playVideo';
import { submitAaTag } from './logics/analytics/playVideo';

const ID_PREFIX = 'primeVideo';
const PROGRESS_RATE = 1;

/**
 * Props
 * @param movieId 動画ID
 * @param jobId 求人ID
 */
export interface Props {
  movieId: string;
  jobId: string;
}

const reducer: Reducer<typeof initialState, ActionType> = (state, action) => ({
  ...state,
  [action.type]: true,
});

export const DodaPrime: FC<Props> = ({ movieId, jobId }) => {
  const [ratioFlags, dispatch] = useReducer(reducer, initialState);
  const [ratio, setRatio] = useState<null | number>(null);
  const hasFinishedPlaying = useRef(false);
  const idName = `${ID_PREFIX}-${jobId}`;
  const [accessor, setAccessor] = useState<VideoAccessor>();
  const videoRef = useRef<HTMLDivElement>(null);
  // 画面に入るタイミングを監視する。
  const [interSectionRef, inView] = useInView({ triggerOnce: false });

  // jstream CDN 読み込み
  useEffect(() => {
    const scriptTagElement = document.querySelector('#js-embedJstream');
    // 既にscriptタグが存在する場合は return
    if (scriptTagElement) return;
    const head = document.getElementsByTagName('head')[0] as HTMLElement;
    const scriptUrl = document.createElement('script');
    scriptUrl.id = 'js-embedJstream';
    scriptUrl.type = 'text/javascript';
    scriptUrl.src = getPrimeVideoSetting().PRIME_MOVIE_JSTREAM_PLAYER_JS_URL;
    head.appendChild(scriptUrl);
  }, []);

  useEffect(() => {
    const iframe = videoRef.current?.querySelector('iframe');

    if (inView && accessor) {
      accessor.addEventListener('landing', () => {
        accessor.play();
      });
    }
    // videoがない場合は埋め込む
    if (inView && !iframe && window.navigator.onLine) {
      const loop = window.setInterval(() => {
        // jstream_t3: CDNで読み込まれるメソッド
        if (typeof window.jstream_t3 !== 'undefined') {
          setAccessor(insertVideo(movieId, idName).accessor);
          window.clearInterval(loop);
        }
      }, 300);
    }
    // 画面から出ていて、消せる状態の場合、負荷対策としてビデオを削除する
    if (!inView && accessor?.state) deleteVideo(iframe);
    // TODO: 修正してdisableを取り除く
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, accessor]);

  useEffect(() => {
    if (!accessor) return;
    accessor.progressRate = PROGRESS_RATE; // progressイベントの実行頻度
    // 全て動画を再生した場合
    accessor?.addEventListener('complete', () => {
      if (hasFinishedPlaying.current) return;
      submitAaTag(String(PERCENTAGE.COMPLETE), jobId);
      hasFinishedPlaying.current = true;
    });

    // 割合を計算するコールバック関数
    const callback = () => {
      const target = videoRef.current?.querySelector('iframe');
      // 動画が画面外の時はイベントを削除する
      if (!target) {
        accessor.removeEventListener('progress', callback);
        return;
      }
      // 動画再生の割合を算出
      const calculatedRatio =
        // eslint-disable-next-line no-unsafe-optional-chaining
        accessor?.getCurrentTime() / accessor?.getTotalTime();
      setRatio(calculatedRatio);
    };
    // 1秒おきにコールバック関数を実行
    accessor?.addEventListener('progress', callback);
    // TODO: 修正してdisableを取り除く
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessor]);

  useEffect(() => {
    // 割合が到達したら、アクセス解析を発火
    (Object.keys(percentageArrival) as (keyof PercentageArrival)[]).forEach(
      (key) => {
        if (ratio && ratio >= percentageArrival[key] && !ratioFlags[key]) {
          submitAaTag(String(percentageArrival[key]), jobId);
          dispatch({ type: key });
        }
      }
    );
    // TODO: 修正してdisableを取り除く
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratio]);

  return (
    <div ref={interSectionRef} className={styles.dodaPrime} data-mid={movieId}>
      <div ref={videoRef} id={idName} className={styles.dodaPrime__video} />
    </div>
  );
};
